import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import type { editor as MonacoEditor } from 'monaco-editor';
import { Subscription } from 'rxjs';
import { ThemeService } from 'app/shared/services/theme.service';
import { LIGHT_JAVASCRIPT_LANGUAGE } from './light-javascript-language';
import { CODE_FONT_FAMILY, applyCodeEditorTheme } from './code-editor-theme';

@Component({
  selector: 'app-code-editor',
  templateUrl: './code-editor.component.html',
  styleUrl: './code-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CodeEditorComponent), multi: true }],
})
export class CodeEditorComponent implements ControlValueAccessor {
  readonly value = model<string>('');
  readonly language = input('javascript');
  readonly lineNumbers = input(false);
  readonly wordWrap = input(false);
  readonly autoFocus = input(false);
  readonly invalid = input(false);
  readonly height = input<number>();
  readonly minHeight = input(96);
  readonly maxHeight = input(420);
  readonly label = input('');

  protected readonly loading = signal(true);
  protected readonly control = new FormControl('', { nonNullable: true });
  protected readonly editorHeight = computed(
    () => this.height() ?? Math.min(Math.max(this.contentHeight(), this.minHeight()), this.maxHeight()),
  );
  protected readonly options = computed<MonacoEditor.IStandaloneEditorConstructionOptions>(() => ({
    language: this.language() === 'javascript' ? LIGHT_JAVASCRIPT_LANGUAGE : this.language(),
    lineNumbers: this.lineNumbers() ? 'on' : 'off',
    lineNumbersMinChars: 3,
    lineDecorationsWidth: this.lineNumbers() ? 8 : 4,
    folding: this.lineNumbers(),
    glyphMargin: false,
    wordWrap: this.wordWrap() ? 'on' : 'off',
    minimap: { enabled: false },
    automaticLayout: true,
    fixedOverflowWidgets: true,
    scrollBeyondLastLine: false,
    renderLineHighlight: 'none',
    overviewRulerLanes: 0,
    overviewRulerBorder: false,
    hideCursorInOverviewRuler: true,
    scrollbar: { alwaysConsumeMouseWheel: false, verticalScrollbarSize: 10, horizontalScrollbarSize: 10 },
    padding: { top: 8, bottom: 8 },
    fontSize: 13,
    fontFamily: CODE_FONT_FAMILY,
    tabSize: 2,
    ariaLabel: this.label(),
  }));

  private readonly themeService = inject(ThemeService);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly destroyRef = inject(DestroyRef);
  private readonly contentHeight = signal(0);
  private themeSubscription: Subscription | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    effect(() => {
      const value = this.value() ?? '';
      if (value !== this.control.value) {
        this.control.setValue(value, { emitEvent: false });
      }
    });
    this.control.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (value === this.value()) {
        return;
      }
      this.value.set(value);
      this.onChange(value);
    });
    this.destroyRef.onDestroy(() => this.themeSubscription?.unsubscribe());
  }

  protected onEditorInit(editor: MonacoEditor.IStandaloneCodeEditor): void {
    this.loading.set(false);
    this.contentHeight.set(editor.getContentHeight());
    editor.onDidContentSizeChange(() => this.contentHeight.set(editor.getContentHeight()));
    editor.onDidBlurEditorWidget(() => this.onTouched());
    this.themeSubscription?.unsubscribe();
    this.themeSubscription = this.themeService.isDarkThemeBS.subscribe((isDark) =>
      applyCodeEditorTheme(this.host, !!isDark),
    );
    if (this.autoFocus()) {
      editor.focus();
    }
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }
}
